const API_BASE = import.meta.env.VITE_API_URL || '/api/v1'

async function request<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const token = localStorage.getItem('admin_token')
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }

  let res: Response
  try {
    res = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers: { ...headers, ...(options?.headers as Record<string, string>) },
    })
  } catch {
    throw new Error('Серверт холбогдох боломжгүй байна. Интернэт холболтоо шалгана уу.')
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    if ((res.status === 401 || res.status === 403) && !endpoint.includes('/admin/auth/login')) {
      localStorage.removeItem('admin_token')
      window.location.href = res.status === 403 ? '/login?error=forbidden' : '/login'
    }
    throw new Error(body.message || `Серверийн алдаа (${res.status})`)
  }

  return res.json()
}

function qs(params?: Record<string, string | undefined>) {
  if (!params) return ''
  const clean = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== '')
  ) as Record<string, string>
  return new URLSearchParams(clean).toString()
}

export const api = {
  admin: {
    // Auth
    login: (data: { email: string; password: string }) =>
      request<{ token: string; user: Record<string, unknown> }>('/admin/auth/login', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    register: (data: { firstName: string; lastName: string; email: string; password: string }) =>
      request<{ token: string; user: Record<string, unknown> }>('/admin/auth/register', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    getMe: () =>
      request<{ user: Record<string, unknown>; permissions: string[] }>('/admin/auth/me'),

    getMyPermissions: async () => {
      const res = await request<{ user: { isSuperAdmin: boolean }; permissions: string[] }>('/admin/auth/me')
      return {
        isSuperAdmin: res.user?.isSuperAdmin ?? false,
        permissions: res.permissions ?? [],
      }
    },

    // Dashboard
    dashboard: () => request<any>('/admin/dashboard'),

    // Bookings
    getBookings: (params?: Record<string, string>) =>
      request<any>(`/admin/bookings?${qs(params)}`),
    getBooking: (id: string) =>
      request<any>(`/admin/bookings/${id}`),
    createBooking: (data: Record<string, unknown>) => {
      const { startTime, endTime, ...rest } = data
      const payload: Record<string, unknown> = {
        ...rest,
        timeSlot: { startTime, endTime },
      }
      return request<any>('/admin/bookings', { method: 'POST', body: JSON.stringify(payload) })
    },
    updateBooking: (id: string, data: Record<string, unknown>) =>
      request<any>(`/admin/bookings/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    updateBookingStatus: (id: string, data: { status: string; cancelReason?: string }) =>
      request<any>(`/admin/bookings/${id}/status`, { method: 'PUT', body: JSON.stringify(data) }),
    cancelBooking: (id: string, data: Record<string, unknown>) =>
      request<any>(`/admin/bookings/${id}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status: 'cancelled', cancelReason: data.cancelReason }),
      }),

    // Services
    getServices: (params?: Record<string, string>) =>
      request<any>(`/admin/services?${qs(params)}`),
    getService: (id: string) =>
      request<any>(`/admin/services/${id}`),
    createService: (data: Record<string, unknown>) =>
      request<any>('/admin/services', { method: 'POST', body: JSON.stringify(data) }),
    updateService: (id: string, data: Record<string, unknown>) =>
      request<any>(`/admin/services/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    deleteService: (id: string) =>
      request<any>(`/admin/services/${id}`, { method: 'DELETE' }),

    // Customers
    getCustomers: (params?: Record<string, string>) =>
      request<any>(`/admin/customers?${qs(params)}`),
    getCustomer: (id: string) =>
      request<any>(`/admin/customers/${id}`),
    createCustomer: (data: Record<string, unknown>) =>
      request<any>('/admin/customers', { method: 'POST', body: JSON.stringify(data) }),
    updateCustomer: (id: string, data: Record<string, unknown>) =>
      request<any>(`/admin/customers/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    deleteCustomer: (id: string) =>
      request<any>(`/admin/customers/${id}`, { method: 'DELETE' }),

    // System Users
    getSystemUsers: (params?: Record<string, string>) =>
      request<any>(`/admin/system-users?${qs(params)}`),
    createSystemUser: (data: Record<string, unknown>) =>
      request<any>('/admin/system-users', { method: 'POST', body: JSON.stringify(data) }),
    updateSystemUser: (id: string, data: Record<string, unknown>) =>
      request<any>(`/admin/system-users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    deleteSystemUser: (id: string) =>
      request<any>(`/admin/system-users/${id}`, { method: 'DELETE' }),

    // Auth - Change Password
    changePassword: (data: { currentPassword: string; newPassword: string }) =>
      request<{ message: string }>('/admin/auth/change-password', {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    // Homepage Content (banners, announcements, sections)
    getHomepageContents: (params?: Record<string, string>) =>
      request<any>(`/admin/homepage-content?${qs(params)}`),
    getHomepageContent: (id: string) =>
      request<any>(`/admin/homepage-content/${id}`),
    createHomepageContent: (data: Record<string, unknown>) =>
      request<any>('/admin/homepage-content', { method: 'POST', body: JSON.stringify(data) }),
    updateHomepageContent: (id: string, data: Record<string, unknown>) =>
      request<any>(`/admin/homepage-content/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    updateHomepageContentStatus: (id: string, status: 'active' | 'inactive') =>
      request<any>(`/admin/homepage-content/${id}/status`, { method: 'PUT', body: JSON.stringify({ status }) }),
    deleteHomepageContent: (id: string) =>
      request<any>(`/admin/homepage-content/${id}`, { method: 'DELETE' }),
    reorderHomepageContents: (ids: string[]) =>
      request<any>('/admin/homepage-content/reorder', { method: 'PUT', body: JSON.stringify({ ids }) }),

    // Website Content (static sections: hero, about, contact)
    getWebsiteContent: () =>
      request<any>('/admin/website-content'),
    updateHeroContent: (data: Record<string, unknown>) =>
      request<any>('/admin/website-content/hero', { method: 'PUT', body: JSON.stringify(data) }),
    updateAboutContent: (data: Record<string, unknown>) =>
      request<any>('/admin/website-content/about', { method: 'PUT', body: JSON.stringify(data) }),
    updateContactContent: (data: Record<string, unknown>) =>
      request<any>('/admin/website-content/contact', { method: 'PUT', body: JSON.stringify(data) }),
    updateCareersContent: (data: Record<string, unknown>) =>
      request<any>('/admin/website-content/careers', { method: 'PUT', body: JSON.stringify(data) }),
    updateSurveyContent: (data: Record<string, unknown>) =>
      request<any>('/admin/website-content/survey', { method: 'PUT', body: JSON.stringify(data) }),
    updateReferralLetterContent: (data: Record<string, unknown>) =>
      request<any>('/admin/website-content/referral-letter', { method: 'PUT', body: JSON.stringify(data) }),

    // Job Postings
    getJobPostings: (params?: Record<string, string>) =>
      request<any>(`/admin/job-postings?${qs(params)}`),
    createJobPosting: (data: Record<string, unknown>) =>
      request<any>('/admin/job-postings', { method: 'POST', body: JSON.stringify(data) }),
    updateJobPosting: (id: string, data: Record<string, unknown>) =>
      request<any>(`/admin/job-postings/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    deleteJobPosting: (id: string) =>
      request<any>(`/admin/job-postings/${id}`, { method: 'DELETE' }),
    reorderJobPostings: (ids: string[]) =>
      request<any>('/admin/job-postings/reorder', { method: 'PUT', body: JSON.stringify({ ids }) }),
    updateJobPostingStatus: (id: string, isActive: boolean) =>
      request<any>(`/admin/job-postings/${id}/status`, { method: 'PUT', body: JSON.stringify({ isActive }) }),

    // FAQs
    getFaqs: (params?: Record<string, string>) =>
      request<any>(`/admin/faqs?${qs(params)}`),
    createFaq: (data: Record<string, unknown>) =>
      request<any>('/admin/faqs', { method: 'POST', body: JSON.stringify(data) }),
    updateFaq: (id: string, data: Record<string, unknown>) =>
      request<any>(`/admin/faqs/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    deleteFaq: (id: string) =>
      request<any>(`/admin/faqs/${id}`, { method: 'DELETE' }),
    reorderFaqs: (ids: string[]) =>
      request<any>('/admin/faqs/reorder', { method: 'PUT', body: JSON.stringify({ ids }) }),
    updateFaqStatus: (id: string, isActive: boolean) =>
      request<any>(`/admin/faqs/${id}/status`, { method: 'PUT', body: JSON.stringify({ isActive }) }),

    // Testimonials
    getTestimonials: (params?: Record<string, string>) =>
      request<any>(`/admin/testimonials?${qs(params)}`),
    createTestimonial: (data: Record<string, unknown>) =>
      request<any>('/admin/testimonials', { method: 'POST', body: JSON.stringify(data) }),
    updateTestimonial: (id: string, data: Record<string, unknown>) =>
      request<any>(`/admin/testimonials/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    deleteTestimonial: (id: string) =>
      request<any>(`/admin/testimonials/${id}`, { method: 'DELETE' }),
    reorderTestimonials: (ids: string[]) =>
      request<any>('/admin/testimonials/reorder', { method: 'PUT', body: JSON.stringify({ ids }) }),
    updateTestimonialStatus: (id: string, isActive: boolean) =>
      request<any>(`/admin/testimonials/${id}/status`, { method: 'PUT', body: JSON.stringify({ isActive }) }),

    // Team Members
    getTeamMembers: (params?: Record<string, string>) =>
      request<any>(`/admin/team?${qs(params)}`),
    createTeamMember: (data: Record<string, unknown>) =>
      request<any>('/admin/team', { method: 'POST', body: JSON.stringify(data) }),
    updateTeamMember: (id: string, data: Record<string, unknown>) =>
      request<any>(`/admin/team/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    deleteTeamMember: (id: string) =>
      request<any>(`/admin/team/${id}`, { method: 'DELETE' }),
    reorderTeamMembers: (ids: string[]) =>
      request<any>('/admin/team/reorder', { method: 'PUT', body: JSON.stringify({ ids }) }),
    updateTeamMemberStatus: (id: string, isActive: boolean) =>
      request<any>(`/admin/team/${id}/status`, { method: 'PUT', body: JSON.stringify({ isActive }) }),

    // Departments
    getDepartments: () =>
      request<any>('/admin/departments'),
    getDepartment: (id: string) =>
      request<any>(`/admin/departments/${id}`),
    createDepartment: (data: Record<string, unknown>) =>
      request<any>('/admin/departments', { method: 'POST', body: JSON.stringify(data) }),
    updateDepartment: (id: string, data: Record<string, unknown>) =>
      request<any>(`/admin/departments/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    updateDepartmentStatus: (id: string, isActive: boolean) =>
      request<any>(`/admin/departments/${id}/status`, { method: 'PUT', body: JSON.stringify({ isActive }) }),
    reorderDepartments: (ids: string[]) =>
      request<any>('/admin/departments/reorder', { method: 'PUT', body: JSON.stringify({ ids }) }),
    deleteDepartment: (id: string) =>
      request<any>(`/admin/departments/${id}`, { method: 'DELETE' }),

    // Doctors
    getDoctors: (params?: Record<string, string>) =>
      request<any>(`/admin/doctors?${qs(params)}`),
    getDoctor: (id: string) =>
      request<any>(`/admin/doctors/${id}`),
    createDoctor: (data: Record<string, unknown>) =>
      request<any>('/admin/doctors', { method: 'POST', body: JSON.stringify(data) }),
    updateDoctor: (id: string, data: Record<string, unknown>) =>
      request<any>(`/admin/doctors/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    updateDoctorStatus: (id: string, isActive: boolean) =>
      request<any>(`/admin/doctors/${id}/status`, { method: 'PUT', body: JSON.stringify({ isActive }) }),
    deleteDoctor: (id: string) =>
      request<any>(`/admin/doctors/${id}`, { method: 'DELETE' }),

    // Rooms
    getRooms: (params?: Record<string, string>) =>
      request<any>(`/admin/rooms?${qs(params)}`),
    getRoom: (id: string) => request<any>(`/admin/rooms/${id}`),
    createRoom: (data: Record<string, unknown>) =>
      request<any>('/admin/rooms', { method: 'POST', body: JSON.stringify(data) }),
    updateRoom: (id: string, data: Record<string, unknown>) =>
      request<any>(`/admin/rooms/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    updateRoomStatus: (id: string, isActive: boolean) =>
      request<any>(`/admin/rooms/${id}/status`, { method: 'PUT', body: JSON.stringify({ isActive }) }),
    deleteRoom: (id: string) => request<any>(`/admin/rooms/${id}`, { method: 'DELETE' }),

    // Room Bookings
    getRoomBookings: (params?: Record<string, string>) =>
      request<any>(`/admin/room-bookings?${qs(params)}`),
    getRoomBooking: (id: string) => request<any>(`/admin/room-bookings/${id}`),
    updateRoomBookingStatus: (id: string, status: string, cancelReason?: string) =>
      request<any>(`/admin/room-bookings/${id}/status`, { method: 'PUT', body: JSON.stringify({ status, cancelReason }) }),
    deleteRoomBooking: (id: string) => request<any>(`/admin/room-bookings/${id}`, { method: 'DELETE' }),

    // Packages
    getPackages: (params?: Record<string, string>) =>
      request<any>(`/admin/packages?${qs(params)}`),
    getPackage: (id: string) => request<any>(`/admin/packages/${id}`),
    createPackage: (data: Record<string, unknown>) =>
      request<any>('/admin/packages', { method: 'POST', body: JSON.stringify(data) }),
    updatePackage: (id: string, data: Record<string, unknown>) =>
      request<any>(`/admin/packages/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    updatePackageStatus: (id: string, isActive: boolean) =>
      request<any>(`/admin/packages/${id}/status`, { method: 'PUT', body: JSON.stringify({ isActive }) }),
    deletePackage: (id: string) => request<any>(`/admin/packages/${id}`, { method: 'DELETE' }),

    // About: Board / Values / Milestones / Certifications / Partners
    getBoard: () => request<any>('/admin/board'),
    createBoard: (data: Record<string, unknown>) => request<any>('/admin/board', { method: 'POST', body: JSON.stringify(data) }),
    updateBoard: (id: string, data: Record<string, unknown>) => request<any>(`/admin/board/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    updateBoardStatus: (id: string, isActive: boolean) => request<any>(`/admin/board/${id}/status`, { method: 'PUT', body: JSON.stringify({ isActive }) }),
    deleteBoard: (id: string) => request<any>(`/admin/board/${id}`, { method: 'DELETE' }),
    reorderBoard: (ids: string[]) => request<any>('/admin/board/reorder', { method: 'PUT', body: JSON.stringify({ ids }) }),

    getValues: () => request<any>('/admin/values'),
    createValue: (data: Record<string, unknown>) => request<any>('/admin/values', { method: 'POST', body: JSON.stringify(data) }),
    updateValue: (id: string, data: Record<string, unknown>) => request<any>(`/admin/values/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    updateValueStatus: (id: string, isActive: boolean) => request<any>(`/admin/values/${id}/status`, { method: 'PUT', body: JSON.stringify({ isActive }) }),
    deleteValue: (id: string) => request<any>(`/admin/values/${id}`, { method: 'DELETE' }),
    reorderValues: (ids: string[]) => request<any>('/admin/values/reorder', { method: 'PUT', body: JSON.stringify({ ids }) }),

    getMilestones: () => request<any>('/admin/milestones'),
    createMilestone: (data: Record<string, unknown>) => request<any>('/admin/milestones', { method: 'POST', body: JSON.stringify(data) }),
    updateMilestone: (id: string, data: Record<string, unknown>) => request<any>(`/admin/milestones/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    updateMilestoneStatus: (id: string, isActive: boolean) => request<any>(`/admin/milestones/${id}/status`, { method: 'PUT', body: JSON.stringify({ isActive }) }),
    deleteMilestone: (id: string) => request<any>(`/admin/milestones/${id}`, { method: 'DELETE' }),
    reorderMilestones: (ids: string[]) => request<any>('/admin/milestones/reorder', { method: 'PUT', body: JSON.stringify({ ids }) }),

    getCertifications: () => request<any>('/admin/certifications'),
    createCertification: (data: Record<string, unknown>) => request<any>('/admin/certifications', { method: 'POST', body: JSON.stringify(data) }),
    updateCertification: (id: string, data: Record<string, unknown>) => request<any>(`/admin/certifications/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    updateCertificationStatus: (id: string, isActive: boolean) => request<any>(`/admin/certifications/${id}/status`, { method: 'PUT', body: JSON.stringify({ isActive }) }),
    deleteCertification: (id: string) => request<any>(`/admin/certifications/${id}`, { method: 'DELETE' }),
    reorderCertifications: (ids: string[]) => request<any>('/admin/certifications/reorder', { method: 'PUT', body: JSON.stringify({ ids }) }),

    getPartners: () => request<any>('/admin/partners'),
    createPartner: (data: Record<string, unknown>) => request<any>('/admin/partners', { method: 'POST', body: JSON.stringify(data) }),
    updatePartner: (id: string, data: Record<string, unknown>) => request<any>(`/admin/partners/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    updatePartnerStatus: (id: string, isActive: boolean) => request<any>(`/admin/partners/${id}/status`, { method: 'PUT', body: JSON.stringify({ isActive }) }),
    deletePartner: (id: string) => request<any>(`/admin/partners/${id}`, { method: 'DELETE' }),
    reorderPartners: (ids: string[]) => request<any>('/admin/partners/reorder', { method: 'PUT', body: JSON.stringify({ ids }) }),

    // Insurance Partners
    getInsurancePartners: () => request<any>('/admin/insurance-partners'),
    createInsurancePartner: (data: Record<string, unknown>) => request<any>('/admin/insurance-partners', { method: 'POST', body: JSON.stringify(data) }),
    updateInsurancePartner: (id: string, data: Record<string, unknown>) => request<any>(`/admin/insurance-partners/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    updateInsurancePartnerStatus: (id: string, isActive: boolean) => request<any>(`/admin/insurance-partners/${id}/status`, { method: 'PUT', body: JSON.stringify({ isActive }) }),
    deleteInsurancePartner: (id: string) => request<any>(`/admin/insurance-partners/${id}`, { method: 'DELETE' }),
    reorderInsurancePartners: (ids: string[]) => request<any>('/admin/insurance-partners/reorder', { method: 'PUT', body: JSON.stringify({ ids }) }),

    // Insurance Steps
    getInsuranceSteps: () => request<any>('/admin/insurance-steps'),
    createInsuranceStep: (data: Record<string, unknown>) => request<any>('/admin/insurance-steps', { method: 'POST', body: JSON.stringify(data) }),
    updateInsuranceStep: (id: string, data: Record<string, unknown>) => request<any>(`/admin/insurance-steps/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    updateInsuranceStepStatus: (id: string, isActive: boolean) => request<any>(`/admin/insurance-steps/${id}/status`, { method: 'PUT', body: JSON.stringify({ isActive }) }),
    deleteInsuranceStep: (id: string) => request<any>(`/admin/insurance-steps/${id}`, { method: 'DELETE' }),
    reorderInsuranceSteps: (ids: string[]) => request<any>('/admin/insurance-steps/reorder', { method: 'PUT', body: JSON.stringify({ ids }) }),

    // Branches
    getBranches: () => request<any>('/admin/branches'),
    createBranch: (data: Record<string, unknown>) => request<any>('/admin/branches', { method: 'POST', body: JSON.stringify(data) }),
    updateBranch: (id: string, data: Record<string, unknown>) => request<any>(`/admin/branches/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    updateBranchStatus: (id: string, isActive: boolean) => request<any>(`/admin/branches/${id}/status`, { method: 'PUT', body: JSON.stringify({ isActive }) }),
    deleteBranch: (id: string) => request<any>(`/admin/branches/${id}`, { method: 'DELETE' }),
    reorderBranches: (ids: string[]) => request<any>('/admin/branches/reorder', { method: 'PUT', body: JSON.stringify({ ids }) }),

    // Service Categories
    getSvcCategories: (params?: Record<string, string>) => request<any>(`/admin/svc-categories?${qs(params)}`),
    createSvcCategory: (data: Record<string, unknown>) => request<any>('/admin/svc-categories', { method: 'POST', body: JSON.stringify(data) }),
    updateSvcCategory: (id: string, data: Record<string, unknown>) => request<any>(`/admin/svc-categories/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    deleteSvcCategory: (id: string) => request<any>(`/admin/svc-categories/${id}`, { method: 'DELETE' }),

    // News
    getNews: (params?: Record<string, string>) =>
      request<any>(`/admin/news?${qs(params)}`),
    getNewsItem: (id: string) => request<any>(`/admin/news/${id}`),
    createNews: (data: Record<string, unknown>) =>
      request<any>('/admin/news', { method: 'POST', body: JSON.stringify(data) }),
    updateNews: (id: string, data: Record<string, unknown>) =>
      request<any>(`/admin/news/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    updateNewsStatus: (id: string, isActive: boolean) =>
      request<any>(`/admin/news/${id}/status`, { method: 'PUT', body: JSON.stringify({ isActive }) }),
    deleteNews: (id: string) => request<any>(`/admin/news/${id}`, { method: 'DELETE' }),

    // Unified Homepage (website нүүр хуудас)
    getHomepage: () =>
      request<{ success: boolean; data: import('../types').UnifiedHomepage }>('/admin/homepage').then(r => (r as any).data),
    updateHomepage: (data: import('../types').UnifiedHomepage) =>
      request<{ success: boolean; data: import('../types').UnifiedHomepage }>('/admin/homepage', {
        method: 'PUT',
        body: JSON.stringify(data),
      }).then(r => (r as any).data),

    // Upload
    uploadImage: async (file: File): Promise<{ url: string }> => {
      const token = localStorage.getItem('admin_token')
      const formData = new FormData()
      formData.append('image', file)
      const res = await fetch(`${API_BASE}/admin/upload/image`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      })
      if (!res.ok) throw new Error('Зураг байршуулахад алдаа гарлаа')
      const json = await res.json()
      return json.data
    },

    uploadFile: async (file: File): Promise<{ url: string }> => {
      const token = localStorage.getItem('admin_token')
      const formData = new FormData()
      formData.append('file', file)
      const res = await fetch(`${API_BASE}/admin/upload/file`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      })
      if (!res.ok) throw new Error('Файл байршуулахад алдаа гарлаа')
      const json = await res.json()
      return json.data
    },

    // DevOps - Nginx
    getNginxConfigs: () =>
      request<any>('/admin/devops/nginx'),
    getNginxConfig: (fileName: string) =>
      request<any>(`/admin/devops/nginx/${fileName}`),
    updateNginxPort: (data: { fileName: string; oldPort: string; newPort: string; target: 'listen' | 'proxy_pass' }) =>
      request<any>('/admin/devops/nginx/port', { method: 'PUT', body: JSON.stringify(data) }),
    reloadNginx: () =>
      request<any>('/admin/devops/nginx/reload', { method: 'POST' }),
    testNginx: () =>
      request<any>('/admin/devops/nginx/test', { method: 'POST' }),
  },
}
